/**
 * Email Limbo API Hooks
 *
 * React Query hooks for fetching and managing email messages in limbo state.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { bulkReviewMessages, fetchLimboCount, fetchLimboMessages, reviewMessage } from "@/data/api/email-limbo";
import { EMAIL_ACCOUNT_KEYS } from "@/hooks/api/use-email-accounts";
import { toast } from "@/lib/toast-service";
import type { LimboMessage, ReviewAction } from "@/types/api/email-limbo";

// ============================================================================
// Query Keys
// ============================================================================

export const LIMBO_QUERY_KEYS = {
    all: ["email-limbo"] as const,
    lists: () => [...LIMBO_QUERY_KEYS.all, "list"] as const,
    list: (skip: number, limit: number, accountId?: string, keyword?: string) => [...LIMBO_QUERY_KEYS.lists(), skip, limit, accountId, keyword] as const,
    count: (accountId?: string) => [...LIMBO_QUERY_KEYS.all, "count", accountId] as const,
};

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Hook to fetch email messages in limbo state
 *
 * @param skip - Number of records to skip (pagination)
 * @param limit - Maximum number of records to return
 * @param accountId - Optional account ID to filter by
 * @param keyword - Optional keyword to search in email content
 * @param options - Additional React Query options
 */
export function useLimboMessages(
    skip: number = 0,
    limit: number = 50,
    accountId?: string,
    keyword?: string,
    options?: {
        enabled?: boolean;
        refetchInterval?: number | false;
    },
) {
    return useQuery({
        queryKey: LIMBO_QUERY_KEYS.list(skip, limit, accountId, keyword),
        queryFn: async () => {
            return await fetchLimboMessages(skip, limit, accountId, keyword);
        },
        enabled: options?.enabled ?? true,
        // Default polling every 30 seconds when tab is active
        refetchInterval: options?.refetchInterval ?? 30000,
        // Keep previous data while fetching new data
        placeholderData: (previousData) => previousData,
    });
}

/**
 * Hook to fetch count of email messages in limbo state
 * Useful for showing badges in navigation/tabs
 *
 * @param accountId - Optional account ID to filter by
 * @param options - Additional React Query options
 */
export function useLimboCount(
    accountId?: string,
    options?: {
        enabled?: boolean;
        refetchInterval?: number | false;
    },
) {
    return useQuery({
        queryKey: LIMBO_QUERY_KEYS.count(accountId),
        queryFn: async (): Promise<number> => {
            return await fetchLimboCount(accountId);
        },
        enabled: options?.enabled ?? true,
        // Poll every 60 seconds for badge updates
        refetchInterval: options?.refetchInterval ?? 60000,
    });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Hook to review a single limbo message (approve or reject)
 * Includes optimistic update for immediate UI feedback
 *
 * @example
 * const { mutate: review } = useReviewMessage();
 * review({ messageId: "123", action: "approve" });
 */
export function useReviewMessage() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ messageId, action, containerId }: { messageId: string; action: ReviewAction; containerId?: string }): Promise<void> => {
            return await reviewMessage(messageId, action, containerId);
        },
        // Optimistic update: immediately remove from list
        onMutate: async ({ messageId }) => {
            // Cancel any outgoing refetches
            await queryClient.cancelQueries({
                queryKey: LIMBO_QUERY_KEYS.lists(),
            });

            // Snapshot the previous value for rollback
            const previousLists = queryClient.getQueriesData({
                queryKey: LIMBO_QUERY_KEYS.lists(),
            });

            // Optimistically remove the message from all limbo lists
            queryClient.setQueriesData({ queryKey: LIMBO_QUERY_KEYS.lists() }, (old: LimboMessage[] | undefined) => {
                if (!old) return old;
                return old.filter((msg) => msg.message_id !== messageId);
            });

            // Optimistically decrement count
            queryClient.setQueriesData({ queryKey: LIMBO_QUERY_KEYS.count() }, (old: number | undefined) => {
                if (old === undefined) return old;
                return Math.max(0, old - 1);
            });

            return { previousLists };
        },
        // On error, rollback to the previous value
        onError: (_err, _variables, context) => {
            if (context?.previousLists) {
                context.previousLists.forEach(([queryKey, data]) => {
                    queryClient.setQueryData(queryKey, data);
                });
            }
            toast.error("Errore durante la revisione dell'email");
        },
        // On success, show toast and invalidate queries
        onSuccess: (_data, variables) => {
            const actionText = variables.action === "approve" ? "approvata" : "rifiutata";
            toast.success(`Email ${actionText} con successo`);

            // Invalidate to refetch fresh data
            queryClient.invalidateQueries({
                queryKey: LIMBO_QUERY_KEYS.all,
            });
            // Invalidate the individual email message cache so the detail panel reflects updated status
            queryClient.invalidateQueries({
                queryKey: EMAIL_ACCOUNT_KEYS.message(variables.messageId),
            });
            // Refresh email counts (review changes limbo and inbox counts)
            queryClient.invalidateQueries({ queryKey: ['email', 'counts'] });
        },
    });
}

/**
 * Hook to bulk review multiple limbo messages
 *
 * @example
 * const { mutate: bulkReview } = useBulkReviewMessages();
 * bulkReview({ messageIds: ["123", "456"], action: "approve" });
 */
export function useBulkReviewMessages() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ messageIds, action }: { messageIds: string[]; action: ReviewAction }) => {
            return await bulkReviewMessages(messageIds, action);
        },
        // Optimistic update: immediately remove from list
        onMutate: async ({ messageIds }) => {
            // Cancel any outgoing refetches
            await queryClient.cancelQueries({
                queryKey: LIMBO_QUERY_KEYS.lists(),
            });

            // Snapshot the previous value for rollback
            const previousLists = queryClient.getQueriesData({
                queryKey: LIMBO_QUERY_KEYS.lists(),
            });

            // Optimistically remove messages from all limbo lists
            queryClient.setQueriesData({ queryKey: LIMBO_QUERY_KEYS.lists() }, (old: LimboMessage[] | undefined) => {
                if (!old) return old;
                const messageIdSet = new Set(messageIds);
                return old.filter((msg) => !messageIdSet.has(msg.message_id));
            });

            // Optimistically decrement count
            queryClient.setQueriesData({ queryKey: LIMBO_QUERY_KEYS.count() }, (old: number | undefined) => {
                if (old === undefined) return old;
                return Math.max(0, old - messageIds.length);
            });

            return { previousLists };
        },
        // On error, rollback to the previous value
        onError: (_err, _variables, context) => {
            if (context?.previousLists) {
                context.previousLists.forEach(([queryKey, data]) => {
                    queryClient.setQueryData(queryKey, data);
                });
            }
            toast.error("Errore durante la revisione delle email");
        },
        // On success, show toast and invalidate queries
        onSuccess: (data, variables) => {
            const actionText = variables.action === "approve" ? "approvate" : "rifiutate";
            const processed = data.processed;
            const failed = data.failed;

            if (failed > 0) {
                toast.warning(`${processed} email ${actionText}, ${failed} errori`);
            } else {
                toast.success(`${processed} email ${actionText} con successo`);
            }

            // Invalidate to refetch fresh data
            queryClient.invalidateQueries({
                queryKey: LIMBO_QUERY_KEYS.all,
            });
            // Invalidate each reviewed email's detail cache
            for (const messageId of variables.messageIds) {
                queryClient.invalidateQueries({
                    queryKey: EMAIL_ACCOUNT_KEYS.message(messageId),
                });
            }
            // Refresh email counts (review changes limbo and inbox counts)
            queryClient.invalidateQueries({ queryKey: ['email', 'counts'] });
        },
    });
}
